import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  DOCUMENT,
  inject,
} from '@angular/core'

/** Used to define an interface that could accept an node and a scrollTop */
declare interface ParentOverflow {
  node: any
  scrollTop: any
}

declare interface ParentNodeWithScrollTop extends Node, ParentNode {
  scrollTop: number | null
}

@Directive({
  selector: 'textarea[ngxAutosize]', // eslint-disable-line
  standalone: true,
})
export class TextareaAutosizeDirective implements OnInit {
  /** Enable autoSize */
  @Input() protected enable: boolean = true

  protected el = inject(ElementRef)

  protected _document = inject<Document>(DOCUMENT)

  /** The height offset of the textarea */
  protected heightOffset: number

  /** The window object */
  private window: Window

  @HostListener('mouseup')
  @HostListener('input')
  @HostListener('ngModelChange')
  public onKeyPress(): void {
    if (this.enable) {
      this.update()
    }
  }

  public ngOnInit(): void {
    if (!this.enable) return

    if (
      !(
        typeof this.window === 'undefined' ||
        typeof this.window.getComputedStyle !== 'function'
      )
    ) {
      const style = this.window.getComputedStyle(this.el.nativeElement, null)

      this.el.nativeElement.style.overflowX = 'hidden'
      this.el.nativeElement.style.wordWrap = 'break-word'

      if (style.resize === 'vertical') {
        this.el.nativeElement.style.resize = 'none'
      } else if (style.resize === 'both') {
        this.el.nativeElement.style.resize = 'horizontal'
      }

      // We get the height offset
      if (style.boxSizing === 'content-box') {
        this.heightOffset = -(
          parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
        )
      } else {
        this.heightOffset =
          parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
      }

      // Fix when a textarea is not on document body and heightOffset is Not a Number
      if (isNaN(this.heightOffset)) {
        this.heightOffset = 0
      }

      this.update()
    }
  }

  /** Update the textarea */
  public update(): void {
    this.resize()

    const styleHeight = Math.round(
      parseFloat(this.el.nativeElement.style.height)
    )
    const computed = window.getComputedStyle(this.el.nativeElement, null)

    // Using offsetHeight as a replacement for computed.height in IE, because IE does not account use of border-box
    const actualHeight =
      computed.boxSizing === 'content-box'
        ? Math.round(parseFloat(computed.height))
        : this.el.nativeElement.offsetHeight

    // The actual height not matching the style height (set via the resize method) indicates that
    // the max-height has been exceeded, in which case the overflow should be allowed.
    if (actualHeight < styleHeight) {
      if (computed.overflowY === 'hidden') {
        this.changeOverflow('scroll')
        this.resize()
      }
    } else {
      // Normally keep overflow set to hidden, to avoid flash of scrollbar as the textarea expands.
      if (computed.overflowY !== 'hidden') {
        this.changeOverflow('hidden')
        this.resize()
      }
    }
  }

  /** Resize the textarea */
  private resize(): void {
    if (this.el.nativeElement.scrollHeight === 0) {
      // If the scrollHeight is 0, then the element probably has display:none or is detached from the DOM.
      return
    }

    const overflows = this.getParentOverflows(this.el.nativeElement)
    const docTop =
      document.documentElement && document.documentElement.scrollTop // Needed for Mobile IE

    this.el.nativeElement.style.height = ''

    const resultHeight =
      Number(this.el.nativeElement.scrollHeight) + Number(this.heightOffset)
    this.el.nativeElement.style.height = `${resultHeight}px`

    // prevents scroll-position jumping
    overflows.forEach((el) => {
      el.node.scrollTop = el.scrollTop
    })

    if (docTop) {
      document.documentElement.scrollTop = docTop
    }
  }

  /** Used to get the parent overflow for prevent jumps */
  private getParentOverflows(el: HTMLElement | Node): ParentOverflow[] {
    const arr: { node: ParentNode; scrollTop: number | null }[] = []

    while (el && el.parentNode) {
      if ((el.parentNode as ParentNodeWithScrollTop).scrollTop) {
        arr.push({
          node: el.parentNode,
          scrollTop: (el.parentNode as ParentNodeWithScrollTop).scrollTop,
        })
      }

      el = el.parentNode
    }

    return arr
  }

  /** Update the overflow of the textarea */
  private changeOverflow(value: string): void {
    // Chrome/Safari-specific fix:
    // When the textarea y-overflow is hidden, Chrome/Safari do not reflow the text to account for the space
    // made available by removing the scrollbar. The following forces the necessary text reflow.
    const width = this.el.nativeElement.style.width
    this.el.nativeElement.style.width = '0px'
    // Force reflow:
    /* eslint-disable no-unused-expressions */
    this.el.nativeElement.offsetWidth
    this.el.nativeElement.style.width = width

    this.el.nativeElement.style.overflowY = value
  }
}
